import { useQuery } from '@tanstack/react-query';
import { Link, Navigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { api, type CourseDetail } from '@/lib/api';
import { courseView, type CourseResume } from '@/lib/courseView';
import { resumeHref, resumeSubtitle, watchedPct } from '@/lib/resume';
import { Card, CardContent } from '@/components/ui/card';
import { LessonRow } from '@/components/LessonRow';
import { ModuleBanner } from '@/components/ModuleBanner';
import { ModuleRow } from '@/components/ModuleRow';
import { ResumeCard } from '@/components/ResumeCard';
import { SearchBar } from '@/components/SearchBar';

/**
 * The course page's "Weiterlernen" card. Same component as Home's, asking a
 * different question — first incomplete lesson of *this* course, rather than the
 * most recently touched lesson anywhere — and naming the module instead of the
 * course, since the course is the page the learner is already on.
 */
function CourseResumeCard({ slug, resume }: { slug: string; resume: CourseResume }) {
  const { lesson } = resume;
  return (
    <ResumeCard
      to={resumeHref({
        courseSlug: slug,
        moduleId: resume.moduleId,
        lessonId: lesson.id,
        lastPositionSeconds: lesson.progress.lastPositionSeconds,
      })}
      title={lesson.title}
      subtitle={resumeSubtitle(
        resume.moduleTitle,
        lesson.progress.lastPositionSeconds,
        lesson.durationSeconds,
      )}
      thumbnailUrl={lesson.thumbnailUrl}
      pct={watchedPct(lesson.progress.lastPositionSeconds, lesson.durationSeconds)}
    />
  );
}

export default function Course() {
  const { slug = '' } = useParams();
  const { data, isLoading, error } = useQuery({
    queryKey: ['course', slug],
    queryFn: () => api.get<CourseDetail>(`/api/courses/${slug}`),
    enabled: slug.length > 0,
    retry: (count, err) => {
      const msg = (err as Error).message;
      if (msg === 'course_locked' || msg === 'course_not_found') return false;
      return count < 2;
    },
  });

  if (isLoading) {
    return (
      <div className="mx-auto max-w-3xl p-6 text-sm text-muted-foreground">Lädt …</div>
    );
  }
  if (error) {
    const msg = (error as Error).message;
    // No cookie / session → not logged in. Send them to the login screen
    // (which offers log-in + the free-course signup) instead of a dead error.
    if (msg === 'not_authenticated') {
      return <Navigate to="/login" replace />;
    }
    if (msg === 'course_locked' || msg === 'course_not_found') {
      return <Navigate to="/" replace />;
    }
    return (
      <div className="mx-auto max-w-3xl p-6 text-sm text-destructive">
        Fehler beim Laden.
      </div>
    );
  }
  if (!data) return null;

  const { course, modules } = data;
  const view = courseView(modules, course.sequentialUnlock);

  // Exactly one module → skip the level entirely and render its lessons here.
  // The free Masterclass is this case (one module, ~14 drip lessons) and it's
  // the funnel conversion path, so it must not gain a tap. The module route
  // still resolves if deep-linked; the lesson page reads the same rule to keep
  // its back-link from pointing at a level this course doesn't show.
  const soleModule = view.soleModule;

  // pb-40 clears the floating bottom nav — repeated on `sm:` so the `sm:p-6`
  // shorthand can't reset it back to 1.5rem.
  return (
    <div className="mx-auto max-w-3xl space-y-6 p-4 pb-40 sm:p-6 sm:pb-40 sm:space-y-8">
      <div className="flex items-center justify-between">
        <Link
          to="/"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="size-4" /> Alle Kurse
        </Link>
      </div>

      <header className="space-y-3">
        <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">{course.title}</h1>
        {course.subtitle && (
          <p className="text-sm text-muted-foreground">{course.subtitle}</p>
        )}
        <p className="text-sm text-muted-foreground">
          {view.completedLessons} von {view.totalLessons} Lektionen abgeschlossen · {view.pct}%
        </p>
        <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
          <div className="h-full bg-primary" style={{ width: `${view.pct}%` }} />
        </div>
        {/* Search stays here and only here — its results deep-link straight to
            `lesson?t=`, jumping past the module level. */}
        <SearchBar courseSlug={slug} />
      </header>

      {/* Straight to the first incomplete lesson, past the module level — the
          module list costs a returning learner a tap, and on Gold it means
          scanning 15 progress bars to find where they left off. Hidden once the
          course is finished: a dead "continue" is worse than none. */}
      {view.resume && <CourseResumeCard slug={slug} resume={view.resume} />}

      {view.modules.length === 0 && (
        <Card>
          <CardContent className="p-6 text-center text-sm text-muted-foreground">
            Hier gibt es noch nichts zu sehen. Schau später wieder vorbei.
          </CardContent>
        </Card>
      )}

      {soleModule ? (
        <section className="space-y-3">
          <ModuleBanner title={soleModule.title} imageUrl={soleModule.imageUrl} />
          {soleModule.description && (
            <p className="text-sm text-muted-foreground">{soleModule.description}</p>
          )}
          <div className="space-y-2">
            {soleModule.lessons.map((l, idx) => (
              <LessonRow
                key={l.id}
                lesson={l}
                number={idx + 1}
                locked={view.lockedLessonIds.has(l.id)}
                to={`/kurse/${slug}/${soleModule.id}/${l.id}`}
              />
            ))}
          </div>
        </section>
      ) : (
        <div className="space-y-2">
          {view.modules.map((s) => (
            <ModuleRow
              key={s.module.id}
              summary={s}
              to={`/kurse/${slug}/${s.module.id}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
